import moment from 'moment-timezone';

import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const { result } = reader;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read attachment'));
        return;
      }

      const [, base64 = ''] = result.split(',');
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read attachment'));
    reader.readAsDataURL(file);
  });

const normalizeReceiverType = (receiverType) => {
  if (!receiverType) return null;
  if (Array.isArray(receiverType)) return receiverType[0] || null;
  return receiverType;
};

const isClinicSender = (senderType) => `${senderType || ''}`.toUpperCase() === 'CLINIC';

export const hasUnreadIncomingMessage = (message, currentUserId) => {
  if (!message || message.isRead) return false;

  const senderType = message.senderType ?? message.sender_type ?? '';
  const senderId = message.senderId ?? message.sender_id ?? null;

  if (!isClinicSender(senderType)) return true;
  return senderId !== currentUserId;
};

export const getReceiverTypeFromRecipients = ({ sendToPatient, sendToCaregiver }) => {
  if (sendToPatient && !sendToCaregiver) return 'patient';
  if (sendToCaregiver && !sendToPatient) return 'caregiver';
  return null;
};

export const getAttachmentPayload = async (attachment) => {
  if (!attachment) return {};

  const fileContent = await readFileAsBase64(attachment);
  return {
    fileContent,
    fileName: attachment.name,
    fileType: attachment.type || 'application/octet-stream',
  };
};

export const sendSecureMessage = async ({
  patientId,
  messageSubject,
  body,
  sendToPatient,
  sendToCaregiver,
  receiverType,
  notificationType,
  attachment,
  threadId,
}) => {
  const attachmentData = await getAttachmentPayload(attachment);
  const response = await apiRequest({
    endpoint: 'patientPortalMessages',
    method: HttpMethods.Post,
    body: {
      patientId,
      messageSubject,
      body,
      sendToPatient,
      sendToCaregiver,
      receiverType:
        getReceiverTypeFromRecipients({ sendToPatient, sendToCaregiver }) ||
        normalizeReceiverType(receiverType),
      notificationType,
      ...(threadId ? { threadId } : {}),
      ...attachmentData,
    },
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(getRestErrorMessage(err) || 'Failed to send message');
  }

  return response.json();
};

export const getNormalizedThreads = (items, currentUserId = null) =>
  items
    .map((item) => {
      const recipients = [];
      if (item.sendToPatient ?? item.send_to_patient) recipients.push('Patient');
      if (item.sendToCaregiver ?? item.send_to_caregiver) recipients.push('Caregiver');
      const isRead = item.isRead ?? item.is_read ?? true;
      const senderType = item.senderType ?? item.sender_type ?? '';
      const senderId = item.senderId ?? item.sender_id ?? null;

      const createdAt = item.sentAt ?? item.sent_at ?? item.createdAt ?? item.created_at;
      const sentAtValue =
        createdAt && moment(createdAt).isValid() ? moment(createdAt).valueOf() : 0;

      return {
        threadId: item.threadId ?? item.thread_id ?? item.id,
        subject: item.messageSubject ?? item.message_subject ?? '-',
        body: item.body ?? '-',
        recipients: recipients.join(', ') || '-',
        sendToPatient: item.sendToPatient ?? item.send_to_patient ?? false,
        sendToCaregiver: item.sendToCaregiver ?? item.send_to_caregiver ?? false,
        senderType,
        senderId,
        isRead,
        hasUnread: hasUnreadIncomingMessage({ isRead, senderType, senderId }, currentUserId),
        sentAt: sentAtValue > 0 ? moment(createdAt).format('MM/DD/YYYY') : '-',
        sentAtValue,
      };
    })
    .sort((a, b) => b.sentAtValue - a.sentAtValue);

export const getNormalizedThreadsFromMessages = (items, currentUserId = null) => {
  const threadGroups = items.reduce((acc, item) => {
    const threadId = item.threadId ?? item.thread_id ?? item.id;
    if (!threadId) return acc;

    if (!acc[threadId]) acc[threadId] = [];
    acc[threadId].push(item);
    return acc;
  }, {});

  return Object.entries(threadGroups)
    .map(([threadId, threadItems]) => {
      const sortedItems = [...threadItems].sort((a, b) => {
        const aValue = moment(a.sentAt ?? a.sent_at ?? a.createdAt ?? a.created_at).valueOf() || 0;
        const bValue = moment(b.sentAt ?? b.sent_at ?? b.createdAt ?? b.created_at).valueOf() || 0;
        return aValue - bValue;
      });

      const rootMessage = sortedItems[0] || {};
      const latestMessage = sortedItems[sortedItems.length - 1] || rootMessage;
      const latestCreatedAt =
        latestMessage.sentAt ??
        latestMessage.sent_at ??
        latestMessage.createdAt ??
        latestMessage.created_at;
      const latestSentAtValue =
        latestCreatedAt && moment(latestCreatedAt).isValid()
          ? moment(latestCreatedAt).valueOf()
          : 0;
      const recipients = [];

      if (rootMessage.sendToPatient ?? rootMessage.send_to_patient) recipients.push('Patient');
      if (rootMessage.sendToCaregiver ?? rootMessage.send_to_caregiver)
        recipients.push('Caregiver');

      return {
        threadId,
        subject: rootMessage.messageSubject ?? rootMessage.message_subject ?? '-',
        body: latestMessage.body ?? rootMessage.body ?? '-',
        recipients: recipients.join(', ') || '-',
        sendToPatient: rootMessage.sendToPatient ?? rootMessage.send_to_patient ?? false,
        sendToCaregiver: rootMessage.sendToCaregiver ?? rootMessage.send_to_caregiver ?? false,
        senderType: latestMessage.senderType ?? latestMessage.sender_type ?? '',
        senderId: latestMessage.senderId ?? latestMessage.sender_id ?? null,
        isRead: !sortedItems.some((message) => hasUnreadIncomingMessage(message, currentUserId)),
        hasUnread: sortedItems.some((message) => hasUnreadIncomingMessage(message, currentUserId)),
        sentAt: latestSentAtValue > 0 ? moment(latestCreatedAt).format('MM/DD/YYYY') : '-',
        sentAtValue: latestSentAtValue,
      };
    })
    .sort((a, b) => b.sentAtValue - a.sentAtValue);
};

export const getNormalizedThreadMessages = (items) =>
  items
    .map((item) => {
      const createdAt = item.sentAt ?? item.sent_at ?? item.createdAt ?? item.created_at;
      const sentAtValue =
        createdAt && moment(createdAt).isValid() ? moment(createdAt).valueOf() : 0;

      return {
        id: item.id,
        messageSubject: item.messageSubject ?? item.message_subject ?? '',
        body: item.body ?? '-',
        senderId: item.senderId ?? item.sender_id ?? null,
        senderType: item.senderType ?? item.sender_type ?? '',
        senderDisplayName: item.senderDisplayName ?? item.sender_display_name ?? null,
        senderCredentials: item.senderCredentials ?? item.sender_credentials ?? null,
        attachmentId: item.attachmentId ?? item.attachment_id ?? null,
        isRead: item.isRead ?? item.is_read ?? false,
        sendToPatient: item.sendToPatient ?? item.send_to_patient ?? false,
        sendToCaregiver: item.sendToCaregiver ?? item.send_to_caregiver ?? false,
        receiverType: normalizeReceiverType(item.receiverType ?? item.receiver_type ?? null),
        notificationType: item.notificationType ?? item.notification_type ?? null,
        sentAt: sentAtValue > 0 ? moment(createdAt).format('MM/DD/YYYY HH:mm') : '-',
        sentAtValue,
      };
    })
    .sort((a, b) => a.sentAtValue - b.sentAtValue);
